// src/firebaseConfig.js

import { initializeApp, getApps } from "firebase/app";
import { getAuth, setPersistence, browserLocalPersistence, GoogleAuthProvider } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getAnalytics, isSupported } from "firebase/analytics";


// Lee la variable REACT_APP_ENV (ej: "testing" o "production")
const env = process.env.REACT_APP_ENV || "production";

// Determinar authDomain basado en el hostname actual
// Esto es necesario para Safari iOS - debe usar dominio propio, no firebaseapp.com
const getAuthDomain = () => {
  // En servidor (SSR) o tests, usar variable de entorno
  if (typeof window === 'undefined') {
    return env === "testing"
      ? process.env.REACT_APP_AUTH_DOMAIN_TEST
      : process.env.REACT_APP_AUTH_DOMAIN_PROD;
  }

  const hostname = window.location.hostname;

  // Desarrollo local
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return env === "testing"
      ? process.env.REACT_APP_AUTH_DOMAIN_TEST
      : process.env.REACT_APP_AUTH_DOMAIN_PROD;
  }

  // UAT/Testing
  if (hostname === 'uat.lemmontags.com' || hostname.includes('uat')) {
    return 'uat.lemmontags.com';
  }

  // Producción
  if (hostname === 'app.lemmontags.com' || hostname.includes('lemmontags.com')) {
    return 'app.lemmontags.com';
  }

  // Fallback a variable de entorno
  return env === "testing"
    ? process.env.REACT_APP_AUTH_DOMAIN_TEST
    : process.env.REACT_APP_AUTH_DOMAIN_PROD;
};

// Determina config según el ambiente
let firebaseConfig;

if (env === "testing") {
  firebaseConfig = {
    apiKey: process.env.REACT_APP_API_KEY_TEST,
    authDomain: getAuthDomain(),
    projectId: process.env.REACT_APP_PROJECT_ID_TEST,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET_TEST,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID_TEST,
    appId: process.env.REACT_APP_APP_ID_TEST,
    databaseURL: process.env.REACT_APP_DATABASE_URL_TEST,
  };
} else {
  firebaseConfig = {
    apiKey: process.env.REACT_APP_API_KEY_PROD,
    authDomain: getAuthDomain(),
    projectId: process.env.REACT_APP_PROJECT_ID_PROD,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET_PROD,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID_PROD,
    appId: process.env.REACT_APP_APP_ID_PROD,
    databaseURL: process.env.REACT_APP_DATABASE_URL_PROD,
    measurementId: "G-29G7R5C7XW", // Google Analytics 4 - solo producción
  };
}

// Log para debug (temporal)
if (typeof window !== 'undefined') {
  console.log('[firebaseConfig] authDomain:', firebaseConfig.authDomain, '| hostname:', window.location.hostname);
}

// Indica si estamos en producción (para habilitar Analytics)
const isProduction = env !== "testing";

// Instancias únicas
let app;
let db;
let auth;
let analytics = null;

/**
 * SIMPLIFICADO: Siempre usa la app DEFAULT para evitar problemas
 * La configuración ya determina a qué proyecto conectarse
 */
export const initFirebase = async () => {
  if (app) {
    return { app, db, auth, analytics };
  }

  try {
    const apps = getApps();
    const existingApp = apps.find((a) => a.name === "[DEFAULT]");

    if (existingApp) {
      app = existingApp;
    } else {
      app = initializeApp(firebaseConfig); // SIEMPRE usa DEFAULT
    }

    db = getFirestore(app);
    auth = getAuth(app);

    await setPersistence(auth, browserLocalPersistence);

    // Inicializar Analytics solo en producción y si el navegador lo soporta
    if (isProduction && firebaseConfig.measurementId) {
      try {
        const supported = await isSupported();
        if (supported) {
          analytics = getAnalytics(app);
        }
      } catch (analyticsError) {
        console.warn("[firebaseConfig] Analytics no disponible:", analyticsError.message);
      }
    }

    return { app, db, auth, analytics };

  } catch (error) {
    console.error("[firebaseConfig] Error al inicializar:", error);
    throw new Error(`Error al inicializar Firebase: ${error.message}`);
  }
};

// Exportar auth, googleProvider y analytics directamente para facilitar su uso
export const googleProvider = new GoogleAuthProvider();
export { auth, analytics };